/**
 * Generic utility for stitching multiple image blobs together with separators
 * Framework and application agnostic - works with any image sources
 */

// Default configuration constants
const DEFAULT_IMAGE_FORMAT = 'image/png';
const DEFAULT_IMAGE_QUALITY = 0.95;
const DEFAULT_SEPARATOR_WIDTH = 4; // pixels
const DEFAULT_SEPARATOR_COLOR = '#D1D5DB'; // Tailwind gray-300
const DEFAULT_LAYOUT = 'horizontal';
const DEFAULT_IMAGE_SMOOTHING_QUALITY = 'high';

export interface ScreenshotStitchOptions {
  /** Image format (default: 'image/png') */
  format?: 'image/png' | 'image/jpeg' | 'image/webp';
  /** JPEG quality 0-1 (default: 0.95) */
  quality?: number;
  /** Maximum width for each image (default: no limit) */
  maxWidth?: number;
  /** Maximum height for each image (default: no limit) */
  maxHeight?: number;
  /** Width of separator line between images (default: 4) */
  separatorWidth?: number;
  /** Color of separator line (default: '#D1D5DB' - Tailwind gray-300) */
  separatorColor?: string;
  /** Layout orientation (default: 'horizontal' = side-by-side) */
  layout?: 'horizontal' | 'vertical';
  /** Force all images to be the same size for equal splits (default: false) */
  forceEqualSizes?: boolean;
}

export interface ScreenshotStitchResult {
  /** Base64-encoded image data URL */
  imageData: string;
  /** Width of final image */
  width: number;
  /** Height of final image */
  height: number;
  /** Number of images stitched together */
  imageCount: number;
}

/**
 * Stitches multiple image blobs together with separators
 * @param blobs - Array of image blobs to stitch (can be from any source)
 * @param options - Configuration options for stitching
 * @returns Result with stitched image data and metadata
 * @throws Error if no blobs provided or image processing fails
 *
 * @example
 * // Stitch 2 images side-by-side (vertical split line)
 * const result = await stitchScreenshots([blob1, blob2], {
 *   layout: 'horizontal',
 *   separatorWidth: 4,
 *   separatorColor: '#D1D5DB',
 *   maxWidth: 1920,
 * });
 *
 * @example
 * // Stitch 3 images vertically (horizontal split lines)
 * const result = await stitchScreenshots([blob1, blob2, blob3], {
 *   layout: 'vertical',
 *   separatorWidth: 8,
 *   separatorColor: '#3B82F6',
 * });
 */
export async function stitchScreenshots(
  blobs: Blob[],
  options: ScreenshotStitchOptions = {}
): Promise<ScreenshotStitchResult> {
  if (blobs.length === 0) {
    throw new Error('No image blobs provided');
  }

  const {
    format = DEFAULT_IMAGE_FORMAT,
    quality = DEFAULT_IMAGE_QUALITY,
    maxWidth,
    maxHeight,
    separatorWidth = DEFAULT_SEPARATOR_WIDTH,
    separatorColor = DEFAULT_SEPARATOR_COLOR,
    layout = DEFAULT_LAYOUT,
    forceEqualSizes = false,
  } = options;

  // Convert all blobs to images
  const images = await Promise.all(blobs.map(createImageFromBlob));

  let resizedImages: HTMLCanvasElement[];

  if (forceEqualSizes && images.length > 1) {
    // Compute equal-size target dimensions from original image dimensions
    // to avoid aspect-ratio distortion from a preliminary resize pass.
    const { targetWidth, targetHeight } = computeEqualSizeTargets(
      images,
      maxWidth,
      maxHeight
    );

    resizedImages = images.map((img) =>
      resizeImageToExactSize(img, targetWidth, targetHeight)
    );
  } else {
    resizedImages = images.map((img) => resizeImage(img, maxWidth, maxHeight));
  }

  // If only one image, return it directly
  if (resizedImages.length === 1) {
    const canvas = resizedImages[0];
    const imageData = canvas.toDataURL(format, quality);

    return {
      imageData,
      width: canvas.width,
      height: canvas.height,
      imageCount: 1,
    };
  }

  // Stitch multiple images together
  const stitchedCanvas = stitchImages(
    resizedImages,
    separatorWidth,
    separatorColor,
    layout
  );

  // Convert to data URL
  const imageData = stitchedCanvas.toDataURL(format, quality);

  return {
    imageData,
    width: stitchedCanvas.width,
    height: stitchedCanvas.height,
    imageCount: resizedImages.length,
  };
}

/**
 * Creates an HTMLImageElement from a Blob
 * @param blob - The image blob to convert
 * @returns Promise that resolves to an HTMLImageElement
 * @throws Error if image fails to load
 */
async function createImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url); // Clean up blob URL
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image from blob'));
    };
    img.src = url;
  });
}

/**
 * Computes per-image target dimensions for the forceEqualSizes path.
 * Uses original image dimensions so that per-image aspect-ratio scaling
 * does not leak cross-axis distortion into the shared target.
 *
 * maxWidth / maxHeight are treated as per-image caps, consistent with
 * their documented meaning on {@link ScreenshotStitchOptions}.
 *
 * @param images - Original images (before any resizing)
 * @param maxWidth - Optional per-image width cap
 * @param maxHeight - Optional per-image height cap
 * @returns Target width and height that every image will be resized to
 * @throws Error if computed dimensions are not positive
 */
function computeEqualSizeTargets(
  images: HTMLImageElement[],
  maxWidth?: number,
  maxHeight?: number
): { targetWidth: number; targetHeight: number } {
  const smallestWidth = Math.min(...images.map((img) => img.width));
  const smallestHeight = Math.min(...images.map((img) => img.height));

  const targetWidth = Math.min(maxWidth ?? Infinity, smallestWidth);
  const targetHeight = Math.min(maxHeight ?? Infinity, smallestHeight);

  if (targetWidth <= 0 || targetHeight <= 0) {
    throw new Error(
      `Invalid target dimensions: ${targetWidth}x${targetHeight}. ` +
        `Ensure maxWidth and maxHeight are positive values.`
    );
  }

  return { targetWidth, targetHeight };
}

/**
 * Resizes an image to fit within max dimensions while maintaining aspect ratio
 * @param image - The source image to resize
 * @param maxWidth - Maximum width (optional)
 * @param maxHeight - Maximum height (optional)
 * @returns Canvas containing the resized image
 */
function resizeImage(
  image: HTMLImageElement,
  maxWidth?: number,
  maxHeight?: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  let { width, height } = image;

  // Calculate scaled dimensions if limits are set
  if (maxWidth && width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (maxHeight && height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = DEFAULT_IMAGE_SMOOTHING_QUALITY;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas;
}

/**
 * Resizes an image to exact dimensions (does not maintain aspect ratio)
 * Used when forceEqualSizes is true to ensure all images have identical dimensions
 * @param image - The source image to resize
 * @param targetWidth - Target width in pixels
 * @param targetHeight - Target height in pixels
 * @returns Canvas containing the resized image
 */
function resizeImageToExactSize(
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  canvas.width = Math.round(targetWidth);
  canvas.height = Math.round(targetHeight);

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = DEFAULT_IMAGE_SMOOTHING_QUALITY;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas;
}

/**
 * Stitches multiple images together with separator lines between each
 * @param images - Array of canvas images to stitch
 * @param separatorWidth - Width of separator line in pixels
 * @param separatorColor - CSS color for separator
 * @param layout - Layout orientation ('horizontal' = side-by-side, 'vertical' = top-to-bottom)
 * @returns Canvas containing the stitched images
 */
function stitchImages(
  images: HTMLCanvasElement[],
  separatorWidth: number,
  separatorColor: string,
  layout: 'horizontal' | 'vertical'
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  const separatorCount = images.length - 1;

  if (layout === 'horizontal') {
    // Side-by-side layout (vertical split line between images)
    const totalWidth = images.reduce((sum, img) => sum + img.width, 0);
    const totalSeparatorWidth = separatorCount * separatorWidth;
    canvas.width = totalWidth + totalSeparatorWidth;
    canvas.height = Math.max(...images.map((img) => img.height));

    let currentX = 0;

    // Draw each image with separator
    images.forEach((img, index) => {
      // Draw image
      ctx.drawImage(img, currentX, 0);
      currentX += img.width;

      // Draw separator (except after last image)
      if (index < images.length - 1) {
        ctx.fillStyle = separatorColor;
        ctx.fillRect(currentX, 0, separatorWidth, canvas.height);
        currentX += separatorWidth;
      }
    });
  } else {
    // Vertical layout (horizontal split line between images)
    const totalHeight = images.reduce((sum, img) => sum + img.height, 0);
    const totalSeparatorHeight = separatorCount * separatorWidth;
    canvas.width = Math.max(...images.map((img) => img.width));
    canvas.height = totalHeight + totalSeparatorHeight;

    let currentY = 0;

    // Draw each image with separator
    images.forEach((img, index) => {
      // Draw image
      ctx.drawImage(img, 0, currentY);
      currentY += img.height;

      // Draw separator (except after last image)
      if (index < images.length - 1) {
        ctx.fillStyle = separatorColor;
        ctx.fillRect(0, currentY, canvas.width, separatorWidth);
        currentY += separatorWidth;
      }
    });
  }

  return canvas;
}
